import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Droplets, Sparkles, Brain, Heart } from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { usePeriodStore } from '../../store/periodStore';

export function CyclePrediction() {
  const { predictNextPeriod, predictOvulation } = usePeriodStore();

  const nextPeriod = predictNextPeriod();
  const ovulation = predictOvulation();

  const predictions = [
    {
      title: 'Next Period',
      date: nextPeriod ? format(nextPeriod, 'MMM d') : 'N/A',
      icon: <Droplets className="w-5 h-5 text-primary" />,
      description: 'Prepare for your next cycle',
      daysUntil: nextPeriod ? Math.ceil((nextPeriod.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0
    },
    {
      title: 'Ovulation',
      date: ovulation ? format(ovulation, 'MMM d') : 'N/A',
      icon: <Sparkles className="w-5 h-5 text-purple-500" />,
      description: 'Peak fertility window',
      daysUntil: ovulation ? Math.ceil((ovulation.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0
    },
    {
      title: 'Fertile Window',
      date: ovulation ? `${format(subDays(ovulation, 3), 'MMM d')} - ${format(addDays(ovulation, 3), 'MMM d')}` : 'N/A',
      icon: <Heart className="w-5 h-5 text-blue-500" />,
      description: 'High chance of conception',
      daysUntil: ovulation ? Math.ceil((subDays(ovulation, 3).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0
    },
    {
      title: 'PMS Likely',
      date: nextPeriod ? format(subDays(nextPeriod, 7), 'MMM d') : 'N/A',
      icon: <Brain className="w-5 h-5 text-pink-500" />,
      description: 'Monitor mood changes',
      daysUntil: nextPeriod ? Math.ceil((subDays(nextPeriod, 7).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0
    }
  ];

  return (
    <div className="theme-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="relative">
          <Calendar className="w-6 h-6 text-primary" />
          <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
        </div>
        <h2 className="text-lg font-semibold">Upcoming Events</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {predictions.map((prediction, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-muted/30 p-4 rounded-lg space-y-3"
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                {prediction.icon}
                <div className="absolute inset-0 bg-current opacity-20 blur-xl rounded-full" />
              </div>
              <div>
                <div className="font-medium">{prediction.title}</div>
                <div className="text-sm text-muted-foreground">{prediction.date}</div>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{prediction.description}</span>
              {prediction.daysUntil > 0 && (
                <span className="text-primary">in {prediction.daysUntil} days</span>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}